import path from "node:path";
import { Buffer } from "node:buffer";

import { z } from "zod";

import {
  customTool,
  type LlmMediaResolution,
  type LlmToolOutputContentItem,
  type LlmCustomTool,
  type LlmFunctionTool,
  type LlmToolSet,
  tool,
} from "../llm.js";
import {
  applyPatch,
  CODEX_APPLY_PATCH_FREEFORM_TOOL_DESCRIPTION,
  CODEX_APPLY_PATCH_LARK_GRAMMAR,
  CODEX_APPLY_PATCH_INPUT_DESCRIPTION,
} from "./applyPatch.js";
import {
  createNodeAgentFilesystem,
  type AgentDirectoryEntry,
  type AgentFilesystem,
} from "./filesystem.js";

const DEFAULT_READ_FILE_LINE_LIMIT = 2000;
const DEFAULT_LIST_DIR_LIMIT = 25;
const DEFAULT_LIST_DIR_DEPTH = 2;
const DEFAULT_GREP_LIMIT = 100;
const MAX_GREP_LIMIT = 2000;
const MAX_VIEW_IMAGE_BYTES = 10 * 1024 * 1024;
const DEFAULT_MAX_LINE_LENGTH = 500;
const DEFAULT_GREP_MAX_SCANNED_FILES = 20_000;
const SUPPORTED_IMAGE_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif"]);
const IMAGE_MIME_BY_EXTENSION: Readonly<Record<string, string>> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".gif": "image/gif",
};

type ListEntryRecord = {
  readonly name: string;
  readonly displayName: string;
  readonly depth: number;
  readonly kind: AgentDirectoryEntry["kind"];
};

type GrepMatchRecord = {
  readonly filePath: string;
  readonly mtimeMs: number;
  readonly lineNumber?: number;
  readonly line?: string;
};

export type AgentFilesystemToolProfile = "auto" | "model-agnostic" | "codex" | "gemini";

export type AgentFilesystemToolName =
  | "apply_patch"
  | "read_file"
  | "write_file"
  | "replace"
  | "list_dir"
  | "list_directory"
  | "grep_files"
  | "view_image"
  | "rg_search"
  | "grep_search"
  | "glob";

export type AgentFilesystemToolAction = "read" | "write" | "delete" | "move" | "list" | "search";

export type AgentFilesystemToolAccessContext = {
  readonly cwd: string;
  readonly tool: AgentFilesystemToolName;
  readonly action: AgentFilesystemToolAction;
  readonly path: string;
  readonly fromPath?: string;
  readonly toPath?: string;
  readonly pattern?: string;
  readonly include?: string;
};

export type AgentFilesystemToolAccessHook = (
  context: AgentFilesystemToolAccessContext,
) => Promise<void> | void;

export type AgentFilesystemToolsOptions = {
  readonly cwd?: string;
  readonly fs?: AgentFilesystem;
  readonly allowOutsideCwd?: boolean;
  readonly checkAccess?: AgentFilesystemToolAccessHook;
  readonly mediaResolution?: LlmMediaResolution;
  readonly maxLineLength?: number;
  readonly grepMaxScannedFiles?: number;
  readonly applyPatch?: {
    readonly maxPatchBytes?: number;
  };
};

function parseOptionalString(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }
  return trimmed;
}

const codexReadFileInputSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }
    const record = value as Record<string, unknown>;
    const filePath = parseOptionalString(record.file_path);
    const aliasPath = parseOptionalString(record.path);
    if (!filePath && aliasPath) {
      return {
        ...record,
        file_path: aliasPath,
      };
    }
    return value;
  },
  z
    .object({
      file_path: z.preprocess(
        (value) => parseOptionalString(value),
        z
          .string()
          .min(1)
          .describe(
            "Path to the file (relative to cwd, or absolute. In sandbox mode, / maps to the sandbox root).",
          ),
      ),
      path: z.preprocess(
        (value) => parseOptionalString(value),
        z
          .string()
          .min(1)
          .optional()
          .describe(
            "Alias for file_path. If both file_path and path are provided they must be identical.",
          ),
      ),
      offset: z
        .number()
        .int()
        .min(1)
        .nullish()
        .describe("The line number to start reading from. Must be 1 or greater."),
      limit: z.number().int().min(1).nullish().describe("The maximum number of lines to return."),
    })
    .strict()
    .superRefine((value, context) => {
      const aliasPath = value.path?.trim() ?? "";
      if (aliasPath.length > 0 && value.file_path !== aliasPath) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "file_path and path must match when both are provided.",
          path: ["path"],
        });
      }
    }),
);

const codexListDirInputSchema = z.object({
  dir_path: z
    .string()
    .min(1)
    .describe(
      "Path to the directory to list (relative to cwd, or absolute. In sandbox mode, / maps to the sandbox root).",
    ),
  offset: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe("The entry number to start listing from. Must be 1 or greater."),
  limit: z.number().int().min(1).nullish().describe("The maximum number of entries to return."),
  depth: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe("The maximum directory depth to traverse. Must be 1 or greater."),
});

const codexGrepFilesInputSchema = z.object({
  pattern: z.string().min(1).describe("Regular expression pattern to search for."),
  include: z
    .string()
    .nullish()
    .describe('Optional glob limiting searched files (for example "*.rs").'),
  path: z.string().nullish().describe("Directory or file path to search. Defaults to cwd."),
  limit: z
    .number()
    .int()
    .min(1)
    .nullish()
    .describe("Maximum number of file paths to return (defaults to 100)."),
});

const codexViewImageInputSchema = z.object({
  path: z.string().min(1).describe("Local filesystem path to an image file"),
});

const applyPatchInputSchema = z.object({
  input: z.string().min(1).describe(CODEX_APPLY_PATCH_INPUT_DESCRIPTION),
});

const geminiReadFileInputSchema = z
  .object({
    file_path: z.string().min(1),
    offset: z.number().int().min(0).nullish(),
    limit: z.number().int().min(1).nullish(),
  })
  .strict();

const geminiWriteFileInputSchema = z.object({
  file_path: z.string().min(1),
  content: z.string(),
});

const geminiReplaceInputSchema = z.object({
  file_path: z.string().min(1),
  instruction: z.string().min(1),
  old_string: z.string(),
  new_string: z.string(),
  expected_replacements: z.number().int().min(1).nullish(),
});

const geminiListDirectoryInputSchema = z.object({
  dir_path: z.string().min(1),
  ignore: z.array(z.string()).nullish(),
  file_filtering_options: z
    .object({
      respect_git_ignore: z.boolean().nullish(),
      respect_gemini_ignore: z.boolean().nullish(),
    })
    .nullish(),
});

const geminiRgSearchInputSchema = z.object({
  pattern: z.string().min(1),
  path: z.string().nullish(),
  glob: z.string().nullish(),
  case_sensitive: z.boolean().nullish(),
  exclude_pattern: z.string().nullish(),
  names_only: z.boolean().nullish(),
  max_matches_per_file: z.number().int().min(1).nullish(),
  max_results: z.number().int().min(1).nullish(),
});

const geminiGrepSearchInputSchema = z.object({
  pattern: z.string().min(1),
  dir_path: z.string().nullish(),
  include: z.string().nullish(),
  exclude_pattern: z.string().nullish(),
  names_only: z.boolean().nullish(),
  max_matches_per_file: z.number().int().min(1).nullish(),
  total_max_matches: z.number().int().min(1).nullish(),
});

const geminiGlobInputSchema = z.object({
  pattern: z.string().min(1),
  dir_path: z.string().nullish(),
  case_sensitive: z.boolean().nullish(),
  respect_git_ignore: z.boolean().nullish(),
  respect_gemini_ignore: z.boolean().nullish(),
});

export type CodexReadFileToolInput = z.output<typeof codexReadFileInputSchema>;
export type CodexListDirToolInput = z.output<typeof codexListDirInputSchema>;
export type CodexGrepFilesToolInput = z.output<typeof codexGrepFilesInputSchema>;
export type CodexViewImageToolInput = z.output<typeof codexViewImageInputSchema>;
export type CodexApplyPatchToolInput = z.output<typeof applyPatchInputSchema>;
export type GeminiReadFileToolInput = z.output<typeof geminiReadFileInputSchema>;
export type GeminiWriteFileToolInput = z.output<typeof geminiWriteFileInputSchema>;
export type GeminiReplaceToolInput = z.output<typeof geminiReplaceInputSchema>;
export type GeminiListDirectoryToolInput = z.output<typeof geminiListDirectoryInputSchema>;
export type GeminiGrepSearchToolInput = z.output<typeof geminiGrepSearchInputSchema>;
export type GeminiRgSearchToolInput = z.output<typeof geminiRgSearchInputSchema>;
export type GeminiGlobToolInput = z.output<typeof geminiGlobInputSchema>;

export function resolveFilesystemToolProfile(
  model: string,
  profile: AgentFilesystemToolProfile = "auto",
): Exclude<AgentFilesystemToolProfile, "auto"> {
  if (profile !== "auto") {
    return profile;
  }
  if (isCodexModel(model)) {
    return "codex";
  }
  if (isGeminiModel(model)) {
    return "gemini";
  }
  return "model-agnostic";
}

export function createFilesystemToolSetForModel(
  model: string,
  profileOrOptions: AgentFilesystemToolProfile | AgentFilesystemToolsOptions = "auto",
  maybeOptions?: AgentFilesystemToolsOptions,
): LlmToolSet {
  if (typeof profileOrOptions === "string") {
    const resolvedProfile = resolveFilesystemToolProfile(model, profileOrOptions);
    if (resolvedProfile === "codex") {
      return createCodexFilesystemToolSet(maybeOptions);
    }
    if (resolvedProfile === "gemini") {
      return createGeminiFilesystemToolSet(maybeOptions);
    }
    return createModelAgnosticFilesystemToolSet(maybeOptions);
  }

  const resolvedProfile = resolveFilesystemToolProfile(model, "auto");
  if (resolvedProfile === "codex") {
    return createCodexFilesystemToolSet(profileOrOptions);
  }
  if (resolvedProfile === "gemini") {
    return createGeminiFilesystemToolSet(profileOrOptions);
  }
  return createModelAgnosticFilesystemToolSet(profileOrOptions);
}

export function createCodexFilesystemToolSet(
  options: AgentFilesystemToolsOptions = {},
): LlmToolSet {
  return {
    apply_patch: createCodexApplyPatchTool(options),
    read_file: createCodexReadFileTool(options),
    list_dir: createListDirTool(options),
    grep_files: createGrepFilesTool(options),
    view_image: createViewImageTool(options),
  };
}

export function createGeminiFilesystemToolSet(
  options: AgentFilesystemToolsOptions = {},
): LlmToolSet {
  return {
    read_file: createGeminiReadFileTool(options),
    write_file: createWriteFileTool(options),
    replace: createReplaceTool(options),
    list_directory: createListDirectoryTool(options),
    grep_search: createGrepSearchTool(options),
    glob: createGlobTool(options),
  };
}

export function createModelAgnosticFilesystemToolSet(
  options: AgentFilesystemToolsOptions = {},
): LlmToolSet {
  return createGeminiFilesystemToolSet(options);
}

export function createCodexApplyPatchTool(
  options: AgentFilesystemToolsOptions = {},
): LlmCustomTool<string> {
  return customTool({
    description: CODEX_APPLY_PATCH_FREEFORM_TOOL_DESCRIPTION,
    format: {
      type: "grammar",
      syntax: "lark",
      definition: CODEX_APPLY_PATCH_LARK_GRAMMAR,
    },
    execute: async (input) => {
      const runtime = resolveRuntime(options);
      const result = await applyPatch({
        patch: input,
        cwd: runtime.cwd,
        fs: runtime.filesystem,
        allowOutsideCwd: runtime.allowOutsideCwd,
        checkAccess: runtime.checkAccess
          ? async (context) => {
              await runtime.checkAccess?.({
                cwd: runtime.cwd,
                tool: "apply_patch",
                action: mapApplyPatchAction(context.kind),
                path: context.path,
                fromPath: context.fromPath,
                toPath: context.toPath,
              });
            }
          : undefined,
        maxPatchBytes: options.applyPatch?.maxPatchBytes,
      });
      return result.summary;
    },
  });
}

export function createCodexReadFileTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof codexReadFileInputSchema, string> {
  return tool({
    description: "Reads a local UTF-8 text file with 1-indexed line numbers.",
    inputSchema: codexReadFileInputSchema,
    execute: async (input) => readFileCodex(input, options),
  });
}

export function createListDirTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof codexListDirInputSchema, string> {
  return tool({
    description:
      "Lists entries in a local directory with 1-indexed entry numbers and simple type labels.",
    inputSchema: codexListDirInputSchema,
    execute: async (input) => listDirectoryCodex(input, options),
  });
}

export function createGrepFilesTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof codexGrepFilesInputSchema, string> {
  return tool({
    description:
      "Finds files whose contents match the pattern and lists them by modification time.",
    inputSchema: codexGrepFilesInputSchema,
    execute: async (input) => grepFilesCodex(input, options),
  });
}

export function createViewImageTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof codexViewImageInputSchema, LlmToolOutputContentItem[]> {
  return tool({
    description:
      "View a local image from the filesystem (only use if given a full filepath by the user, and the image isn't already attached to the thread context within <image ...> tags).",
    inputSchema: codexViewImageInputSchema,
    execute: async (input) => viewImageCodex(input, options),
  });
}

export function createGeminiReadFileTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiReadFileInputSchema, string> {
  return tool({
    description:
      "Reads and returns the content of a specified file. Supports optional 0-based line offset and line limit.",
    inputSchema: geminiReadFileInputSchema,
    execute: async (input) => readFileGemini(input, options),
  });
}

export function createWriteFileTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiWriteFileInputSchema, string> {
  return tool({
    description: "Writes content to a specified file in the local filesystem.",
    inputSchema: geminiWriteFileInputSchema,
    execute: async (input) => writeFileGemini(input, options),
  });
}

export function createReplaceTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiReplaceInputSchema, string> {
  return tool({
    description: "Replaces exact literal text within a file.",
    inputSchema: geminiReplaceInputSchema,
    execute: async (input) => replaceFileContentGemini(input, options),
  });
}

export function createListDirectoryTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiListDirectoryInputSchema, string> {
  return tool({
    description: "Lists files and subdirectories directly within a specified directory path.",
    inputSchema: geminiListDirectoryInputSchema,
    execute: async (input) => listDirectoryGemini(input, options),
  });
}

export function createGrepSearchTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiGrepSearchInputSchema, string> {
  return tool({
    description: "Searches for a regex pattern within file contents.",
    inputSchema: geminiGrepSearchInputSchema,
    execute: async (input) => grepSearchGemini(input, options),
  });
}

export function createRgSearchTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiRgSearchInputSchema, string> {
  return tool({
    description: "Searches for a regex pattern within file contents.",
    inputSchema: geminiRgSearchInputSchema,
    execute: async (input) => rgSearchGemini(input, options),
  });
}

export function createGlobTool(
  options: AgentFilesystemToolsOptions = {},
): LlmFunctionTool<typeof geminiGlobInputSchema, string> {
  return tool({
    description: "Finds files matching glob patterns, sorted by modification time (newest first).",
    inputSchema: geminiGlobInputSchema,
    execute: async (input) => globFilesGemini(input, options),
  });
}

function resolveCodexReadFilePath(input: CodexReadFileToolInput): string {
  const filePath = parseOptionalString(input.file_path);
  if (filePath) {
    return filePath;
  }
  const aliasPath = parseOptionalString(input.path);
  if (aliasPath) {
    return aliasPath;
  }
  throw new Error("read_file requires file_path");
}

async function readFileCodex(
  input: CodexReadFileToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const filePath = resolvePathWithPolicy(
    resolveCodexReadFilePath(input),
    runtime.cwd,
    runtime.allowOutsideCwd,
  );
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "read_file",
    action: "read",
    path: filePath,
  });

  const fileBytes = await readBinaryFile(runtime.filesystem, filePath);
  const imageMimeType = detectImageMimeType(fileBytes, filePath);
  if (imageMimeType) {
    throw new Error(
      `read_file only supports text files; "${toDisplayPath(filePath, runtime.cwd)}" is an image (${imageMimeType}). Use view_image instead.`,
    );
  }
  if (isPdfFile(fileBytes, filePath)) {
    throw new Error(
      `read_file only supports text files; "${toDisplayPath(filePath, runtime.cwd)}" is a PDF.`,
    );
  }
  if (!isValidUtf8(fileBytes)) {
    throw new Error(
      `read_file only supports UTF-8 text files; "${toDisplayPath(filePath, runtime.cwd)}" appears to be binary.`,
    );
  }

  const content = fileBytes.toString("utf8");
  const lines = splitLines(content);
  const offset = input.offset ?? 1;
  const limit = input.limit ?? DEFAULT_READ_FILE_LINE_LIMIT;
  if (offset > lines.length) {
    throw new Error("offset exceeds file length");
  }

  const output: string[] = [];
  const lastLine = Math.min(lines.length, offset + limit - 1);
  for (let lineNumber = offset; lineNumber <= lastLine; lineNumber += 1) {
    const line = lines[lineNumber - 1] ?? "";
    output.push(`L${lineNumber}: ${truncateAtCodePointBoundary(line, runtime.maxLineLength)}`);
  }
  return output.join("\n");
}

async function listDirectoryCodex(
  input: CodexListDirToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const dirPath = resolvePathWithPolicy(input.dir_path, runtime.cwd, runtime.allowOutsideCwd);
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "list_dir",
    action: "list",
    path: dirPath,
  });

  const stats = await runtime.filesystem.stat(dirPath);
  if (stats.kind !== "directory") {
    throw new Error(`failed to read directory: "${dirPath}" is not a directory`);
  }

  const offset = input.offset ?? 1;
  const limit = input.limit ?? DEFAULT_LIST_DIR_LIMIT;
  const depth = input.depth ?? DEFAULT_LIST_DIR_DEPTH;
  const entries = await collectDirectoryEntries(
    runtime.filesystem,
    dirPath,
    depth,
    runtime.maxLineLength,
  );
  if (offset > entries.length) {
    throw new Error("offset exceeds directory entry count");
  }

  const startIndex = offset - 1;
  const remaining = entries.length - startIndex;
  const cappedLimit = Math.min(limit, remaining);
  const selected = entries.slice(startIndex, startIndex + cappedLimit);

  const output: string[] = [`Absolute path: ${toSandboxDisplayPath(dirPath, runtime.cwd)}`];
  for (const entry of selected) {
    output.push(formatListEntry(entry));
  }
  if (startIndex + cappedLimit < entries.length) {
    output.push(`More than ${cappedLimit} entries found`);
  }

  return output.join("\n");
}

async function grepFilesCodex(
  input: CodexGrepFilesToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const pattern = input.pattern.trim();
  if (pattern.length === 0) {
    throw new Error("pattern must not be empty");
  }
  const regex = compileRegex(pattern);
  const searchPath = resolvePathWithPolicy(
    input.path ?? runtime.cwd,
    runtime.cwd,
    runtime.allowOutsideCwd,
  );
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "grep_files",
    action: "search",
    path: searchPath,
    pattern,
    include: input.include?.trim(),
  });

  const searchPathInfo = await runtime.filesystem.stat(searchPath);
  const filesToScan = await collectSearchFiles({
    filesystem: runtime.filesystem,
    searchPath,
    rootKind: searchPathInfo.kind,
    maxScannedFiles: runtime.grepMaxScannedFiles,
  });

  const includeMatcher = input.include ? createGlobMatcher(input.include) : null;
  const matches: GrepMatchRecord[] = [];
  for (const filePath of filesToScan) {
    const relativePath = toDisplayPath(filePath, runtime.cwd);
    if (includeMatcher && !includeMatcher(relativePath)) {
      continue;
    }
    const fileContent = await runtime.filesystem.readTextFile(filePath);
    if (!regex.test(fileContent)) {
      continue;
    }
    const stats = await runtime.filesystem.stat(filePath);
    matches.push({ filePath: normalizeSlashes(relativePath), mtimeMs: stats.mtimeMs });
  }

  if (matches.length === 0) {
    return "No matches found.";
  }

  matches.sort((left, right) => right.mtimeMs - left.mtimeMs);
  const limit = Math.min(input.limit ?? DEFAULT_GREP_LIMIT, MAX_GREP_LIMIT);
  return matches
    .slice(0, limit)
    .map((match) => match.filePath)
    .join("\n");
}

async function viewImageCodex(
  input: CodexViewImageToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<LlmToolOutputContentItem[]> {
  const runtime = resolveRuntime(options);
  const imagePath = resolvePathWithPolicy(input.path, runtime.cwd, runtime.allowOutsideCwd);
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "view_image",
    action: "read",
    path: imagePath,
  });

  const stats = await runtime.filesystem.stat(imagePath);
  if (stats.kind !== "file") {
    throw new Error(`image path \`${toDisplayPath(imagePath, runtime.cwd)}\` is not a file`);
  }

  const bytes = await readBinaryFile(runtime.filesystem, imagePath);
  if (bytes.byteLength > MAX_VIEW_IMAGE_BYTES) {
    return [
      {
        type: "input_text",
        text: `Codex cannot attach image at \`${toDisplayPath(imagePath, runtime.cwd)}\`: image exceeds ${MAX_VIEW_IMAGE_BYTES} bytes.`,
      },
    ];
  }

  const mimeType = detectImageMimeType(bytes, imagePath);
  if (!mimeType) {
    return [
      {
        type: "input_text",
        text: `Codex cannot attach image at \`${toDisplayPath(imagePath, runtime.cwd)}\`: unsupported image format.`,
      },
    ];
  }

  return [
    {
      type: "input_image",
      image_url: `data:${mimeType};base64,${bytes.toString("base64")}`,
      ...(options.mediaResolution ? { detail: options.mediaResolution } : {}),
    },
  ];
}

async function readBinaryFile(filesystem: AgentFilesystem, filePath: string): Promise<Buffer> {
  if (typeof filesystem.readBinaryFile === "function") {
    return await filesystem.readBinaryFile(filePath);
  }
  const text = await filesystem.readTextFile(filePath);
  return Buffer.from(text, "utf8");
}

function detectImageMimeType(buffer: Buffer, filePath: string): string | undefined {
  if (
    buffer.length >= 8 &&
    buffer[0] === 0x89 &&
    buffer[1] === 0x50 &&
    buffer[2] === 0x4e &&
    buffer[3] === 0x47 &&
    buffer[4] === 0x0d &&
    buffer[5] === 0x0a &&
    buffer[6] === 0x1a &&
    buffer[7] === 0x0a
  ) {
    return "image/png";
  }
  if (buffer.length >= 3 && buffer[0] === 0xff && buffer[1] === 0xd8 && buffer[2] === 0xff) {
    return "image/jpeg";
  }
  if (buffer.length >= 6) {
    const signature = buffer.subarray(0, 6).toString("ascii");
    if (signature === "GIF87a" || signature === "GIF89a") {
      return "image/gif";
    }
  }
  if (
    buffer.length >= 12 &&
    buffer.subarray(0, 4).toString("ascii") === "RIFF" &&
    buffer.subarray(8, 12).toString("ascii") === "WEBP"
  ) {
    return "image/webp";
  }
  const fromExtension = IMAGE_MIME_BY_EXTENSION[path.extname(filePath).toLowerCase()];
  if (fromExtension && SUPPORTED_IMAGE_MIME_TYPES.has(fromExtension)) {
    return fromExtension;
  }
  return undefined;
}

function isPdfFile(buffer: Buffer, filePath: string): boolean {
  if (buffer.length >= 5 && buffer.subarray(0, 5).toString("ascii") === "%PDF-") {
    return true;
  }
  return path.extname(filePath).toLowerCase() === ".pdf";
}

function isValidUtf8(buffer: Buffer): boolean {
  if (buffer.length === 0) {
    return true;
  }
  return Buffer.from(buffer.toString("utf8"), "utf8").equals(buffer);
}

async function readFileGemini(
  input: GeminiReadFileToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const filePath = resolvePathWithPolicy(input.file_path, runtime.cwd, runtime.allowOutsideCwd);
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "read_file",
    action: "read",
    path: filePath,
  });

  const content = await runtime.filesystem.readTextFile(filePath);
  const lines = splitLines(content);
  const offset = Math.max(0, input.offset ?? 0);
  const limit = input.limit ?? DEFAULT_READ_FILE_LINE_LIMIT;
  if (offset >= lines.length) {
    return "";
  }

  const end = Math.min(lines.length, offset + limit);
  return lines
    .slice(offset, end)
    .map(
      (line, index) =>
        `L${offset + index + 1}: ${truncateAtCodePointBoundary(line ?? "", runtime.maxLineLength)}`,
    )
    .join("\n");
}

async function writeFileGemini(
  input: GeminiWriteFileToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const filePath = resolvePathWithPolicy(input.file_path, runtime.cwd, runtime.allowOutsideCwd);
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "write_file",
    action: "write",
    path: filePath,
  });
  await runtime.filesystem.ensureDir(path.dirname(filePath));
  await runtime.filesystem.writeTextFile(filePath, input.content);
  return `Successfully wrote file: ${toDisplayPath(filePath, runtime.cwd)}`;
}

async function replaceFileContentGemini(
  input: GeminiReplaceToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const filePath = resolvePathWithPolicy(input.file_path, runtime.cwd, runtime.allowOutsideCwd);
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "replace",
    action: "write",
    path: filePath,
  });

  const expectedReplacements = input.expected_replacements ?? 1;
  const oldValue = input.old_string;
  const newValue = input.new_string;

  let originalContent = "";
  try {
    originalContent = await runtime.filesystem.readTextFile(filePath);
  } catch (error) {
    if (isNoEntError(error) && oldValue.length === 0) {
      await runtime.filesystem.ensureDir(path.dirname(filePath));
      await runtime.filesystem.writeTextFile(filePath, newValue);
      return `Successfully wrote new file: ${toDisplayPath(filePath, runtime.cwd)}`;
    }
    throw error;
  }

  if (oldValue === newValue) {
    throw new Error("No changes to apply. old_string and new_string are identical.");
  }

  const occurrences = countOccurrences(originalContent, oldValue);
  if (occurrences === 0) {
    throw new Error("Failed to edit, could not find old_string in file.");
  }
  if (occurrences !== expectedReplacements) {
    throw new Error(
      `Failed to edit, expected ${expectedReplacements} occurrence(s) but found ${occurrences}.`,
    );
  }
  const updatedContent = safeReplaceAll(originalContent, oldValue, newValue);
  await runtime.filesystem.writeTextFile(filePath, updatedContent);
  return `Successfully replaced ${occurrences} occurrence(s) in ${toDisplayPath(filePath, runtime.cwd)}.`;
}

async function listDirectoryGemini(
  input: GeminiListDirectoryToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const dirPath = resolvePathWithPolicy(input.dir_path, runtime.cwd, runtime.allowOutsideCwd);
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "list_directory",
    action: "list",
    path: dirPath,
  });

  const stats = await runtime.filesystem.stat(dirPath);
  if (stats.kind !== "directory") {
    throw new Error(`Path is not a directory: ${dirPath}`);
  }

  const entries = await runtime.filesystem.readDir(dirPath);
  const ignoreMatchers = (input.ignore ?? []).map((pattern) => createGlobMatcher(pattern));
  const filtered = entries
    .filter((entry) => {
      if (ignoreMatchers.length === 0) {
        return true;
      }
      return !ignoreMatchers.some((matches) => matches(entry.name));
    })
    .sort((left, right) => left.name.localeCompare(right.name));

  if (filtered.length === 0) {
    return `Directory ${toDisplayPath(dirPath, runtime.cwd)} is empty.`;
  }

  return filtered
    .map((entry) => {
      const label = entry.kind === "directory" ? `${entry.name}/` : entry.name;
      return label;
    })
    .join("\n");
}

async function rgSearchGemini(
  input: GeminiRgSearchToolInput,
  options: AgentFilesystemToolsOptions,
  toolName: "rg_search" | "grep_search" = "rg_search",
): Promise<string> {
  const runtime = resolveRuntime(options);
  const pattern = input.pattern.trim();
  if (pattern.length === 0) {
    throw new Error("pattern must not be empty");
  }

  const glob = input.glob?.trim();
  const searchPath = resolvePathWithPolicy(
    input.path ?? runtime.cwd,
    runtime.cwd,
    runtime.allowOutsideCwd,
  );
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: toolName,
    action: "search",
    path: searchPath,
    pattern,
    include: glob,
  });

  const searchPathInfo = await runtime.filesystem.stat(searchPath);
  const filesToScan = await collectSearchFiles({
    filesystem: runtime.filesystem,
    searchPath,
    rootKind: searchPathInfo.kind,
    maxScannedFiles: runtime.grepMaxScannedFiles,
  });

  const matcher = glob ? createGlobMatcher(glob) : null;
  const patternRegex = compileRegex(pattern, input.case_sensitive === true ? "m" : "im");
  const excludeRegex = input.exclude_pattern ? compileRegex(input.exclude_pattern) : null;
  const totalMaxMatches = input.max_results ?? DEFAULT_GREP_LIMIT;
  const perFileMaxMatches = input.max_matches_per_file ?? Number.POSITIVE_INFINITY;

  const matches: GrepMatchRecord[] = [];
  const fileMatches = new Set<string>();
  for (const filePath of filesToScan) {
    const relativePath = normalizeSlashes(toDisplayPath(filePath, runtime.cwd));
    if (matcher && !matcher(relativePath)) {
      continue;
    }

    const content = await runtime.filesystem.readTextFile(filePath);
    const lines = splitLines(content);
    let fileMatchCount = 0;
    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index] ?? "";
      if (!patternRegex.test(line)) {
        continue;
      }
      if (excludeRegex?.test(line)) {
        continue;
      }
      if (fileMatches.has(relativePath) === false) {
        fileMatches.add(relativePath);
      }
      if (input.names_only) {
        continue;
      }
      matches.push({
        filePath: relativePath,
        mtimeMs: 0,
        lineNumber: index + 1,
        line: line,
      });
      fileMatchCount += 1;
      if (fileMatchCount >= perFileMaxMatches || matches.length >= totalMaxMatches) {
        break;
      }
    }

    if (input.names_only && fileMatches.size >= totalMaxMatches) {
      break;
    }
    if (!input.names_only && matches.length >= totalMaxMatches) {
      break;
    }
  }

  if (input.names_only) {
    if (fileMatches.size === 0) {
      return "No matches found.";
    }
    return [...fileMatches].slice(0, totalMaxMatches).join("\n");
  }

  if (matches.length === 0) {
    return "No matches found.";
  }

  return matches
    .slice(0, totalMaxMatches)
    .map((match) => `${match.filePath}:${match.lineNumber}:${match.line ?? ""}`)
    .join("\n");
}

async function grepSearchGemini(
  input: GeminiGrepSearchToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  return rgSearchGemini(
    {
      pattern: input.pattern,
      path: input.dir_path,
      glob: input.include,
      exclude_pattern: input.exclude_pattern,
      names_only: input.names_only,
      max_matches_per_file: input.max_matches_per_file,
      max_results: input.total_max_matches,
    },
    options,
    "grep_search",
  );
}

async function globFilesGemini(
  input: GeminiGlobToolInput,
  options: AgentFilesystemToolsOptions,
): Promise<string> {
  const runtime = resolveRuntime(options);
  const dirPath = resolvePathWithPolicy(
    input.dir_path ?? runtime.cwd,
    runtime.cwd,
    runtime.allowOutsideCwd,
  );
  await runAccessHook(runtime, {
    cwd: runtime.cwd,
    tool: "glob",
    action: "search",
    path: dirPath,
    pattern: input.pattern,
  });

  const dirStats = await runtime.filesystem.stat(dirPath);
  if (dirStats.kind !== "directory") {
    throw new Error(`Path is not a directory: ${dirPath}`);
  }

  const matcher = createGlobMatcher(input.pattern, input.case_sensitive === true);
  const files = await collectSearchFiles({
    filesystem: runtime.filesystem,
    searchPath: dirPath,
    rootKind: "directory",
    maxScannedFiles: runtime.grepMaxScannedFiles,
  });

  const matched: GrepMatchRecord[] = [];
  for (const filePath of files) {
    const relativePath = normalizeSlashes(path.relative(dirPath, filePath));
    if (!matcher(relativePath)) {
      continue;
    }
    const fileStats = await runtime.filesystem.stat(filePath);
    matched.push({
      filePath,
      mtimeMs: fileStats.mtimeMs,
    });
  }

  if (matched.length === 0) {
    return "No files found.";
  }

  matched.sort((left, right) => right.mtimeMs - left.mtimeMs);
  return matched
    .map((entry) => normalizeSlashes(toDisplayPath(entry.filePath, runtime.cwd)))
    .join("\n");
}

type RuntimeContext = {
  readonly cwd: string;
  readonly filesystem: AgentFilesystem;
  readonly allowOutsideCwd: boolean;
  readonly checkAccess?: AgentFilesystemToolAccessHook;
  readonly maxLineLength: number;
  readonly grepMaxScannedFiles: number;
};

function resolveRuntime(options: AgentFilesystemToolsOptions): RuntimeContext {
  return {
    cwd: path.resolve(options.cwd ?? process.cwd()),
    filesystem: options.fs ?? createNodeAgentFilesystem(),
    allowOutsideCwd: options.allowOutsideCwd === true,
    checkAccess: options.checkAccess,
    maxLineLength: options.maxLineLength ?? DEFAULT_MAX_LINE_LENGTH,
    grepMaxScannedFiles: options.grepMaxScannedFiles ?? DEFAULT_GREP_MAX_SCANNED_FILES,
  };
}

async function runAccessHook(
  runtime: RuntimeContext,
  context: AgentFilesystemToolAccessContext,
): Promise<void> {
  if (!runtime.checkAccess) {
    return;
  }
  await runtime.checkAccess(context);
}

function isCodexModel(model: string): boolean {
  if (model.startsWith("experimental-chatgpt-")) {
    return true;
  }
  const normalized = model.startsWith("chatgpt-") ? model.slice("chatgpt-".length) : model;
  return (
    normalized.includes("codex") ||
    normalized.startsWith("gpt-5.6") ||
    normalized === "gpt-5.5" ||
    normalized === "gpt-5.5-fast" ||
    normalized === "gpt-5.4" ||
    normalized === "gpt-5.4-fast"
  );
}

function isGeminiModel(model: string): boolean {
  return model.startsWith("gemini-");
}

function mapApplyPatchAction(
  action: "add" | "delete" | "update" | "move",
): AgentFilesystemToolAction {
  if (action === "add" || action === "update") {
    return "write";
  }
  if (action === "delete") {
    return "delete";
  }
  return "move";
}

function resolvePathWithPolicy(inputPath: string, cwd: string, allowOutsideCwd: boolean): string {
  const absolutePath = path.isAbsolute(inputPath)
    ? path.resolve(inputPath)
    : path.resolve(cwd, inputPath);
  if (allowOutsideCwd || isPathInsideCwd(absolutePath, cwd)) {
    return absolutePath;
  }

  if (path.isAbsolute(inputPath)) {
    const sandboxRelativePath = inputPath.replace(/^[/\\]+/, "");
    const sandboxRootedPath = path.resolve(cwd, sandboxRelativePath);
    if (isPathInsideCwd(sandboxRootedPath, cwd)) {
      return sandboxRootedPath;
    }
  }
  throw new Error(`path "${inputPath}" resolves outside cwd "${cwd}"`);
}

function isPathInsideCwd(candidatePath: string, cwd: string): boolean {
  const relative = path.relative(cwd, candidatePath);
  return relative === "" || (!relative.startsWith("..") && !path.isAbsolute(relative));
}

function toDisplayPath(absolutePath: string, cwd: string): string {
  const relative = path.relative(cwd, absolutePath);
  if (relative === "") {
    return ".";
  }
  if (!relative.startsWith("..") && !path.isAbsolute(relative)) {
    return relative;
  }
  return absolutePath;
}

function toSandboxDisplayPath(absolutePath: string, cwd: string): string {
  const relative = path.relative(cwd, absolutePath);
  if (relative === "") {
    return "/";
  }
  if (!relative.startsWith("..") && !path.isAbsolute(relative)) {
    return `/${normalizeSlashes(relative)}`;
  }
  return normalizeSlashes(absolutePath);
}

function splitLines(content: string): string[] {
  const normalized = content.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalized.split("\n");
  if (lines.length > 0 && lines[lines.length - 1] === "") {
    lines.pop();
  }
  return lines;
}

function truncateAtCodePointBoundary(value: string, maxLength: number): string {
  if (value.length <= maxLength) {
    return value;
  }
  return Array.from(value).slice(0, maxLength).join("");
}

async function collectDirectoryEntries(
  filesystem: AgentFilesystem,
  rootPath: string,
  depth: number,
  maxLineLength: number,
): Promise<ListEntryRecord[]> {
  const queue: Array<{ path: string; relativePrefix: string; remainingDepth: number }> = [
    { path: rootPath, relativePrefix: "", remainingDepth: depth },
  ];
  const records: ListEntryRecord[] = [];

  while (queue.length > 0) {
    const next = queue.shift();
    if (!next) {
      break;
    }
    const entries = await filesystem.readDir(next.path);
    const nextEntries = [...entries]
      .map((entry) => {
        const relativePath = next.relativePrefix
          ? `${next.relativePrefix}/${entry.name}`
          : entry.name;
        return {
          entry,
          relativePath,
          depth: next.relativePrefix.length === 0 ? 0 : next.relativePrefix.split("/").length,
          sortName: normalizeSlashes(relativePath),
        };
      })
      .sort((left, right) => left.sortName.localeCompare(right.sortName));

    for (const item of nextEntries) {
      if (item.entry.kind === "directory" && next.remainingDepth > 1) {
        queue.push({
          path: item.entry.path,
          relativePrefix: item.relativePath,
          remainingDepth: next.remainingDepth - 1,
        });
      }
      records.push({
        name: item.sortName,
        displayName: truncateAtCodePointBoundary(item.entry.name, maxLineLength),
        depth: item.depth,
        kind: item.entry.kind,
      });
    }
  }

  records.sort((left, right) => left.name.localeCompare(right.name));
  return records;
}

function formatListEntry(entry: ListEntryRecord): string {
  const indent = " ".repeat(entry.depth * 2);
  let name = entry.displayName;
  if (entry.kind === "directory") {
    name += "/";
  } else if (entry.kind === "symlink") {
    name += "@";
  } else if (entry.kind === "other") {
    name += "?";
  }
  return `${indent}${name}`;
}

async function collectSearchFiles(params: {
  filesystem: AgentFilesystem;
  searchPath: string;
  rootKind: AgentDirectoryEntry["kind"];
  maxScannedFiles: number;
}): Promise<string[]> {
  const { filesystem, searchPath, rootKind, maxScannedFiles } = params;
  if (rootKind === "file") {
    return [searchPath];
  }

  const queue: string[] = [searchPath];
  const files: string[] = [];
  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      break;
    }
    const entries = await filesystem.readDir(current);
    for (const entry of entries) {
      if (entry.kind === "directory") {
        queue.push(entry.path);
        continue;
      }
      if (entry.kind !== "file") {
        continue;
      }
      files.push(entry.path);
      if (files.length >= maxScannedFiles) {
        return files;
      }
    }
  }
  return files;
}

function compileRegex(pattern: string, flags = "m"): RegExp {
  try {
    return new RegExp(pattern, flags);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    throw new Error(`invalid regex pattern: ${message}`);
  }
}

function createGlobMatcher(
  pattern: string,
  caseSensitive = false,
): (candidatePath: string) => boolean {
  const expanded = expandBracePatterns(normalizeSlashes(pattern.trim()));
  const flags = caseSensitive ? "" : "i";
  const compiled = expanded.map((entry) => ({
    regex: globToRegex(entry, flags),
    applyToBasename: !entry.includes("/"),
  }));

  return (candidatePath: string) => {
    const normalizedPath = normalizeSlashes(candidatePath);
    const basename = path.posix.basename(normalizedPath);
    return compiled.some((entry) =>
      entry.regex.test(entry.applyToBasename ? basename : normalizedPath),
    );
  };
}

function globToRegex(globPattern: string, flags: string): RegExp {
  let source = "^";
  for (let index = 0; index < globPattern.length; index += 1) {
    const char = globPattern[index];
    const nextChar = globPattern[index + 1];

    if (char === undefined) {
      continue;
    }
    if (char === "*" && nextChar === "*") {
      source += ".*";
      index += 1;
      continue;
    }
    if (char === "*") {
      source += "[^/]*";
      continue;
    }
    if (char === "?") {
      source += "[^/]";
      continue;
    }
    source += escapeRegexCharacter(char);
  }
  source += "$";
  return new RegExp(source, flags);
}

function expandBracePatterns(pattern: string): string[] {
  const start = pattern.indexOf("{");
  if (start === -1) {
    return [pattern];
  }

  let depth = 0;
  let end = -1;
  for (let index = start; index < pattern.length; index += 1) {
    const char = pattern[index];
    if (char === "{") {
      depth += 1;
      continue;
    }
    if (char === "}") {
      depth -= 1;
      if (depth === 0) {
        end = index;
        break;
      }
    }
  }

  if (end === -1) {
    return [pattern];
  }

  const prefix = pattern.slice(0, start);
  const suffix = pattern.slice(end + 1);
  const body = pattern.slice(start + 1, end);
  const variants = splitTopLevel(body, ",");
  const expanded: string[] = [];
  for (const variant of variants) {
    expanded.push(...expandBracePatterns(`${prefix}${variant}${suffix}`));
  }
  return expanded;
}

function splitTopLevel(value: string, separator: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = "";
  for (const char of value) {
    if (char === "{") {
      depth += 1;
      current += char;
      continue;
    }
    if (char === "}") {
      depth = Math.max(0, depth - 1);
      current += char;
      continue;
    }
    if (char === separator && depth === 0) {
      parts.push(current);
      current = "";
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts;
}

function escapeRegexCharacter(char: string): string {
  return /[.*+?^${}()|[\]\\]/u.test(char) ? `\\${char}` : char;
}

function normalizeSlashes(value: string): string {
  return value.replaceAll("\\", "/");
}

function countOccurrences(text: string, search: string): number {
  if (search.length === 0) {
    return 0;
  }
  return text.split(search).length - 1;
}

function safeReplaceAll(text: string, search: string, replacement: string): string {
  if (search.length === 0) {
    return text;
  }
  return text.split(search).join(replacement);
}

function isNoEntError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: unknown }).code === "ENOENT"
  );
}
